import React, {useMemo} from 'react';
import {AutoSizer} from 'react-virtualized';
import {ChartQuery} from 'entities/chart';
import {TableContent} from '@/views/storyboard/charts/trending/TableContent';
import {scalableOutputTypes} from '@/views/storyboard/utils/chartConstants';
import {getScaledChartData} from '@/views/storyboard/utils/calculations/chartScaling';
import {TrendingWordsChartData} from '@/views/storyboard/cellhandlers/TrendingWordsCellHandler';
import {notRenderedKeysArray, sortLegendKeys} from '@/views/storyboard/charts/utils/SortLegendKeys';
import i18n from 'i18n';

interface Props {
    chartId?: number | null;
    data: TrendingWordsChartData[];
    scalingFactor?: string;
    chartQuery: ChartQuery;
    chartTitle: string;
    queryDuration?: string;
    chartSettingsView?: boolean;
}

export const TrendingWordsWrapper = React.memo<Props>((
    {
        chartId = null,
        data,
        scalingFactor,
        chartQuery,
        chartTitle,
        queryDuration = '0',
        chartSettingsView = false,
    },
) => {
    const distinctKeys = useMemo(() => {
        let filteredKeys: string[] = [];

        const getDistinctValues = (key: string): void => {
            if (!filteredKeys.concat(notRenderedKeysArray).includes(key)) {
                filteredKeys.push(key);
            }
        };

        for (let i = 0; i < data.length; i++) {
            Object.keys(data[i]).forEach((key) => getDistinctValues(key));
        }

        // Replace all dots with a lookalike unicode symbol because otherwise they get processed as nested objects
        // by react-boostrap-table-next
        filteredKeys = filteredKeys.map((key) => key.replace('.', '\u2024'));

        if (filteredKeys.length == 0) {
            return filteredKeys
        } else if (filteredKeys[0].includes(' to ')) {
            return sortLegendKeys(filteredKeys)
        } else {
            return [...filteredKeys.slice(1), filteredKeys[0]];
        }
    }, [data]);

    let processedData = data;

    processedData = processedData.map((row: TrendingWordsChartData): TrendingWordsChartData => {
        const rowUndefinedRemoved: Partial<TrendingWordsChartData> = {};

        // Replace all dots with a lookalike unicode symbol because otherwise they get processed as nested objects
        // by react-boostrap-table-next
        Object.keys(row).forEach((key) => {
            const cleanKey = key.replace('.', '\u2024');
            rowUndefinedRemoved[cleanKey as keyof TrendingWordsChartData] = row[key as keyof TrendingWordsChartData];
        });

        // Coalesce all undefined and null values to empty strings
        distinctKeys.forEach((key) => {
            rowUndefinedRemoved[key as keyof TrendingWordsChartData] = rowUndefinedRemoved[key as keyof TrendingWordsChartData] ?? '';
        });

        return rowUndefinedRemoved as TrendingWordsChartData;
    });

    if (scalingFactor && scalableOutputTypes.some(output => output === chartQuery.output)) {
        processedData = getScaledChartData(processedData, distinctKeys, scalingFactor);
    }

    return (
        <>
            <AutoSizer>
                {({width, height}) => (
                    <TableContent
                        width={width}
                        height={height}
                        data={processedData}
                        chartId={chartId}
                        chartTitle={chartTitle}
                        distinctKeys={distinctKeys}
                        chartQuery={chartQuery}
                        aggregationField={
                            ['terms_timeline', 'terms'].some(
                                output => output === chartQuery.output
                            ) ? chartQuery.aggs_item : ''
                        }
                    />
                )}
            </AutoSizer>
            {!chartSettingsView ? (
                <div className="chart-query-time-indicator">
                    <p>
                        {i18n.t('table_chart_query_duration.text', {queryDuration})}
                    </p>
                </div>
            ) : null}
        </>
    );
});

